// External dependencies
import { useEffect } from 'react'
import classnames from 'classnames';
import { Container } from 'reactstrap'
import { Swiper as SwiperWrapper, SwiperSlide } from "swiper/react/swiper-react";
import Swiper, { Navigation } from "swiper";
import { __ } from "@wordpress/i18n";
import { registerBlockType } from "@wordpress/blocks";
import { CheckboxControl, PanelBody } from "@wordpress/components";
import { Fragment } from "@wordpress/element";
import { RichText, InspectorControls } from "@wordpress/block-editor";
import useApi from "../../hooks/useApi";
import Card from "../../components/Card/Card";
import PostSelector from "../../components/Gutenberg/PostSelector";
import { ReactComponent as ArrowIconRight } from "../../assets/arrow-right.svg";
import qs from "query-string";

import "swiper/swiper.scss";

import styles from "./RecommendedReading.module.scss";

Swiper.use([Navigation]);

// Block attributes (custom props)
const attributes = {
    headline: {
        type: 'text',
        default: "Recommended reading"
    },
    manuallySelect: {
        type: 'boolean',
        default: false
    },
    scrollable: {
        type: 'boolean',
        default: true
    },
    readings: {
        type: 'array',
        default: []
    }
}

export const RecommendedReading = (props) => {
    const { headline, scrollable, formikContext = {} } = props;
    const { values = {} } = formikContext || {}

    useEffect(() => {
        setTimeout(() => {
            window.sessionStorage.setItem("formValues", "");
        }, 10)
    }, [values])

    const filter = {
        state: values["location"] || null,
        concern: values.concern,
        outcomes: values.outcomes,
        type: 'publication',
        limit: 5
    };
    const filterQueryString = qs.stringify(filter, { arrayFormat: "bracket" });

    // Get posts from API
    const manuallySelect = props.publications && props.publications.length
    const [{ data = {}, loading }] = useApi(`get/publications?${filterQueryString}`, { manual: manuallySelect });
    const publications = (manuallySelect ? props.publications : data.publications) || []

    if (!publications.length) return null

    const id = 'recommended-reading'
    const settings = {
        slidesPerView: 4,
        spaceBetween: 9,
        modules: [Navigation],
        cssMode: true,
        navigation: {
            prevEl: `.swiper-prev-${id}`,
            nextEl: `.swiper-next-${id}`,
            disabledClass: `${styles.swiperHidden}`,
        },
        breakpoints: {
            992: {
                slidesPerView: 4.2,
            },
            680: {
                slidesPerView: 3.2,
            },
            500: {
                slidesPerView: 2.2,
            },
            0: {
                slidesPerView: 1.4,
            },
        },
    };

    return (
        <div className={classnames(styles.wrapper, "my-4 py-3")}>
            {(publications.length) ?
                <>
                    <div className="typographyWrapper container" style={{ marginBottom: 21 }}>
                        <div className="gatewayFormContent position-relative">
                            <h2 style={{ marginRight: scrollable ? 80 : null }}>{headline}</h2>
                            {scrollable
                                ? <button
                                    aria-label="Swipe next carousel"
                                    className={`swiper-prev swiper-next-${id} ${styles.swiperNext}`}
                                >
                                    <span>
                                        Scroll <ArrowIconRight width={14} stroke="#000" />
                                    </span>
                                </button>
                                : null}
                        </div>
                    </div>
                    <Container>
                        <SwiperWrapper {...settings}>
                            {publications.map((publication, index) => (
                                <SwiperSlide key={index} className={styles.slide}>
                                    <Card data={publication} showFooter={false} />
                                </SwiperSlide>
                            ))}
                        </SwiperWrapper>
                    </Container>
                </>
                : null}
        </div >
    )
}

// Edit function (responsible for wp-admin post edit screen)
const edit = ({ setAttributes, ...props }) => {
    const { headline, manuallySelect, scrollable, readings } = props.attributes;

    return (
        <Fragment>
            <InspectorControls>
                <PanelBody title={__('')}>
                    <CheckboxControl
                        label={__('Scrollable', 'hero')}
                        help={__('If disabled, it will automatically recommend readings based on the form options', 'hero')}
                        onChange={(scrollable) => setAttributes({ scrollable })}
                        checked={scrollable}
                    />
                    <CheckboxControl
                        label={__('Manually choose readings', 'hero')}
                        help={__('If disabled, it will automatically recommend readings based on the form options', 'hero')}
                        onChange={(manuallySelect) => setAttributes({ manuallySelect })}
                        checked={manuallySelect}
                    />
                    {manuallySelect
                        ? <PostSelector
                            value={readings}
                            onChange={(readings) => setAttributes({
                                readings: readings || []
                            })}
                            isMulti
                        />
                        : null}
                </PanelBody>
            </InspectorControls>
            <div className="gutenberg-recommended-reading">
                <RecommendedReading
                    headline={<RichText
                        value={headline}
                        multiline={false}
                        onChange={(headline) => setAttributes({ headline })}
                        placeholder={__('Recommended reading', 'hero')}
                        allowedFormats={[]}
                        keepPlaceholderOnFocus={true}
                    />}
                    publications={manuallySelect ? readings.map((reading) => ({ post_title: reading.post_title, link: reading.link })) : []}
                />
            </div>
        </Fragment>
    );
}

// Save function (responsible for static HTML saved in database)
const save = (props) => {
    const { headline, scrollable, readings, manuallySelect } = props.attributes;
    return (
        <div className="gutenberg-recommended-reading" data-mt-attributes={encodeURIComponent(JSON.stringify({
            headline,
            scrollable,
            publications: manuallySelect ? readings.map((reading) => ({ post_title: reading.post_title, link: reading.link })) : [],
        }))}>
            {/* {manuallySelect
                ? <RecommendedReading
                    headline={<RichText.Content value={headline} />}
                    scrollable={scrollable}
                    publications={manuallySelect ? readings.map((reading) => ({ post_title: reading.post_title, link: reading.link })) : []}
                />
                : null} */}
        </div>
    );
}

/**
 * Every block starts by registering a new block type definition.
 *
 * @see https://developer.wordpress.org/block-editor/developers/block-api/#registering-a-block
 */
registerBlockType("mt/gutenberg-recommended-reading", {

    /**
     * This is the display title for your block, which can be translated with `i18n` functions.
     * The block inserter will show this name.
     */
    title: __("Recommended Reading", "gutenberg-recommended-reading"),

    /**
     * An icon property should be specified to make it easier to identify a block.
     * These can be any of WordPress’ Dashicons, or a custom svg element.
     */
    icon: "align-wide",

    /**
     * Blocks are grouped into categories to help users browse and discover them.
     * The categories provided by core are `common`, `embed`, `formatting`, `layout` and `widgets`.
     */
    category: "widgets",

    /**
     * Optional block extended support features.
     */
    // supports: {
    //     // Removes support for an HTML mode.
    //     html: false
    // },
    attributes,

    /**
     * The edit function describes the structure of your block in the context of the editor.
     * This represents what the editor will render when the block is used.
     *
     * @see https://developer.wordpress.org/block-editor/developers/block-api/block-edit-save/#edit
     *
     * @param {Object} [props] Properties passed from the editor.
     *
     * @return {WPElement} Element to render.
     */
    edit,

    /**
     * The save function defines the way in which the different attributes should be combined
     * into the final markup, which is then serialized by the block editor into `post_content`.
     *
     * @see https://developer.wordpress.org/block-editor/developers/block-api/block-edit-save/#save
     *
     * @return {WPElement} Element to render.
     */
    save
});
